import { arEG, enUS } from "date-fns/locale";
import { MatInputModule } from "@angular/material/input";
import { TranslateModule } from "@ngx-translate/core";
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from "@angular/material/core";
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { MatDatepicker, MatDatepickerInputEvent, MatDatepickerModule } from "@angular/material/datepicker";
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS, provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { Component, SimpleChanges, ViewEncapsulation, forwardRef, inject, input, output } from "@angular/core";

import { DateService } from "src/app/core";

import { IsEnglishDirective } from "../../directives";
import { ValidationHandlerPipe } from "../../pipes";
import { ReactiveFormsBaseComponent } from "../../base-components";

const arabicLocale = arEG;
arabicLocale.code = enUS.code;

export const MY_FORMATS = {
  parse: MAT_DATE_FNS_FORMATS.parse,
  display: {
    ...MAT_DATE_FNS_FORMATS.display,
    dateInput: "dd/MM/yyyy",
    monthYearLabel: "MMMM yyyy",
  },
};

@Component({
  selector: "app-datepicker",
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    ValidationHandlerPipe,
    IsEnglishDirective,
  ],
  templateUrl: "./datepicker.component.html",
  styleUrl: "./datepicker.component.scss",
  encapsulation: ViewEncapsulation.None,
  providers: [
    provideDateFnsAdapter(MY_FORMATS),
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
  ],
})
export class DatepickerComponent extends ReactiveFormsBaseComponent {
  private dateService = inject(DateService);
  private _dateAdapter = inject(DateAdapter<DateFnsAdapter>);

  readonly label = input.required<string>();

  readonly minDate = input<Date | null>();
  readonly maxDate = input<Date | null>();
  readonly readonly = input(false);
  readonly isRangeDate = input(false);
  readonly matErrorClass = input("");
  readonly isMonthPicker = input(false);

  readonly onDateChange = output<string | string[]>();

  startDateRange = new FormControl();
  endDateRange = new FormControl();

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.

    if (changes["isRangeDate"] && changes["isRangeDate"].currentValue) this.resetDateRangeValue();
  }

  setMonthAndYear(normalizedMonthAndYear: any, monthpicker: MatDatepicker<any>) {
    this.control.setValue(normalizedMonthAndYear);
    this.onDateChange.emit(this.control.value);
    monthpicker.close();
  }

  setDateValue(event: MatDatepickerInputEvent<Date>) {
    const dateValue: Date | null = event.value;
    if (!dateValue) return;

    const dateFormatted = this.dateService.utc(dateValue);

    this.control.setValue(dateFormatted);
    this.onDateChange.emit(dateFormatted);
  }

  setRangeDateValue(event: MatDatepickerInputEvent<Date>, type: "start" | "end") {
    const dateValue: Date | null = event.value;
    if (!dateValue) return;

    const dateFormatted = this.dateService.utc(dateValue);

    if (type === "start") {
      this.control.setValue([dateFormatted]);
      this.onDateChange.emit(this.control.value);
    } else {
      const startdateValue = JSON.parse(JSON.stringify(this.control.value));

      this.control.setValue([...startdateValue, dateFormatted]);
      this.onDateChange.emit(this.control.value);
    }
  }

  resetDateRangeValue() {
    this.startDateRange.reset();
    this.endDateRange.reset();
    this.control.setValue([]);
    this.onDateChange.emit(this.control.value);
  }

  setCalendarLocale(isEnglish: boolean) {
    this._dateAdapter.setLocale(isEnglish ? enUS : arabicLocale);
  }

  preventDefault(event: KeyboardEvent) {
    event.preventDefault();
  }
}
